const extend = require('extend');
const history = require('../store/history');
const { doFlash } = require('./flash');
const { refreshToken } = require('./token');

require('isomorphic-fetch');

import Cookies from 'js-cookie';

// This is from https://github.com/github/fetch
// We don't have to use it, but it seemed like a good way to have control, and
// I also liked using .catch() instead of passing a second argument to .then
// for error cases
const checkStatus = function(response, dispatch) {
	const isValidUser = response.status >= 200 && response.status < 300;
	const shouldLogout = response.status === 401 && !response.url.match(/user_token/);
	const shouldWarn = response.status === 403;
	const shouldThrowError = !isValidUser;

	if (shouldLogout) {
		history.push('/logout');
	} else if (shouldWarn) {
		console.warn(`Call to ${ response.url } returned 403 unauthorized`); // eslint-disable-line no-console
	} else if (shouldThrowError) {
		const error = new Error(response ? response.statusText : undefined);

		error.response = response;
		throw error;
	}

	return response;
};

const parseJson = (response) => (response && response.json) ? response.json() : null;

// If you're making an API call to anywhere but our server, you should
// set 'includeToken' to false in the extraOptions.
const fetchJson = function(url, fetchOptions = {}, extraOptions = {}, dispatch = null) {
	let token;

	const defaultFetchOptions = {
		method: 'get',
		body: null,
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-Data-Layer-Token': Cookies.get('token') || null
		}
	};

	const modifiedFetchOptions = extend({}, defaultFetchOptions, fetchOptions);

	if (extraOptions.token) {
		modifiedFetchOptions.headers['Authorization'] = `Bearer ${ extraOptions.token }`;
	} else if (extraOptions.includeToken !== false) {
		token = null;

		try {
			const authItem = JSON.parse(localStorage.getItem('auth'));

			({ token } = authItem);
		} catch (error) {
			null;
		}

		if (token) {
			modifiedFetchOptions.headers['Authorization'] = `Bearer ${ token }`;
		}
	}

	if (fetchOptions.body) {
		modifiedFetchOptions.body = JSON.stringify(fetchOptions.body);
	}

	return fetch(url, modifiedFetchOptions)
		.then(function(response) {
			token = response.headers ? response.headers.get('Authorization') : undefined;

			if (dispatch && token) {
				dispatch(refreshToken(token));
			}

			return response;
		})
		.then(response => checkStatus(response, dispatch))
		.then(parseJson);
};

const fail = (type, message) => ({
	type,
	error: true,
	payload: {
		error: message
	}
});

const success = (type, list) => ({
	type,
	payload: {
		list
	}
});

const doAction = (
	url,
	method,
	body,
	doingActionType,
	didActionType,
	failureMessage = null,
	successMessage = null,
	getSuccessPayload = null
) =>
	function(dispatch, getState) {
		// In testing, `getState().auth` is undefined
		const extraOptions = getState().auth ? { token: getState().auth.token } : {};

		dispatch({ type: doingActionType });

		return fetchJson(
			url,
			{
				method,
				body
			},
			extraOptions,
			dispatch
		).then(
			function(responseJson) {
				let message;

				if (responseJson.status === 'success') {
					if (successMessage) {
						dispatch(doFlash('success', 'Success!', successMessage));
					}

					if (getSuccessPayload) {
						return dispatch({
							type: didActionType,
							payload: getSuccessPayload(responseJson)
						});
					} else {
						return dispatch(success(didActionType, responseJson.data.list));
					}
				} else if (responseJson.error === '401 Forbidden') {
					message = 'API call failed because JWT is expired';
					console.warn(message); // eslint-disable-line no-console
					return Promise.reject(new Error(message));
				} else {
					// The `failureMessage` parameter doesn't need to be present to show a flash message on failure,
					// but if the caller hasn't specified a failure message, they presumably don't want the user to be
					// aware of the success/failure of this action in any way
					if (failureMessage) {
						dispatch(doFlash('error', 'Error!', responseJson.message));
					}

					return dispatch(
						fail(
							didActionType,
							responseJson ? responseJson.message : undefined
						)
					);
				}
			},
			function(error) {
				console.error(error); // eslint-disable-line no-console

				if (failureMessage) {
					dispatch(doFlash('error', 'Error!', failureMessage));
				}

				dispatch(
					fail(
						didActionType,
						error.response ? error.response['statusText'] : undefined
					)
				);

				return Promise.reject(error);
			}
		);
	};

const doActionWithServerValidations = (
	url,
	method,
	body,
	doingActionType,
	didActionType,
	failureMessage,
	successMessage = null,
	getSuccessPayload = null
) =>
	// Some of our POST/PATCH calls use server-side validations, so this function returns validation errors
	// as part of the rejected promise if the API call fails.  The returned error is formatted to be usable
	// by redux-form to properly display errors.
	// This also doesn't show flash messages when the request fails, since redux-form handles notification that
	// there were errors.
	function(dispatch, getState) {
		dispatch({ type: doingActionType });
		// We wrap with an extra promise, so that if there is a failure, no subsequent "then"'s will run after our catch block.
		return new Promise(function(resolve, reject) {
			return fetchJson(url, {
				method,
				body
			}).then(
				function(responseJson) {
					if (responseJson.status === 'success') {
						if (successMessage) {
							dispatch(doFlash('success', 'Success!', successMessage));
						}
						if (getSuccessPayload) {
							dispatch({
								type: didActionType,
								payload: getSuccessPayload(responseJson)
							});
						} else {
							dispatch(success(didActionType, responseJson.data.list));
						}
					} else {
						dispatch(doFlash('error', 'Error!', responseJson.message));
						dispatch(fail(didActionType, responseJson.message));
					}
					return resolve(responseJson.data);
				},
				function(error) {
					dispatch(fail(didActionType, error.response['statusText']));
					return reject(
						error.response ? error.response.json() : undefined
					);
				}
			);
		});
	};

module.exports = {
	doAction,
	doActionWithServerValidations,
	fail,
	fetchJson,
	success
};



// WEBPACK FOOTER //
// ./frontend/a2i/js/actions/index.js